import {
    useEffect, useState, useRef, useCallback,
} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';

import { applyWordsAsCommands } from 'BMapsSrc/cmds/cmds_helpers';
import useEventBrokerSubscription from './common/helpers/useEventBrokerSubscription';
import DialogContentSection from './common/DialogContentSection';
import { DialogCancelButton, DialogSubmitButton } from './common/DialogActionButtons';
import { setSkipGreeting, isSkippingGreeting } from '../util/speech_utils';

export function CommandConsole() {
    const [messages, setMessages] = useState([]);
    const [heard, setHeard] = useState('');
    const [noCommand, setNoCommand] = useState(false);
    const [typedCommand, setTypedCommand] = useState('');
    const messagesEndRef = useRef(null);
    const [shorten, setShorten] = useState(isSkippingGreeting);

    const [compoundNames, setCompoundNames] = useState([]);

    const updateCookie = (evt) => {
        const shortened = evt.target.checked;
        setShorten(shortened);
        setSkipGreeting(shortened);
    };

    const getWorkspaceCompoundNames = useCallback(() => {
        const loadedCompounds = App.Workspace.getLoadedCompounds();
        const names = loadedCompounds
            .map((compound, index) => ({
                index,
                name: `Compound ${index + 1}: ${compound.displayName}`,
                id: compound?.resSpec ? `${compound.resSpec}-${index}` : `no-name-${index}`,
            }))
            .filter((item) => item.name);
        setCompoundNames(names);
    }, []);

    useEffect(() => {
        getWorkspaceCompoundNames();
        EventBroker.subscribe('compoundChanged', getWorkspaceCompoundNames);
        return () => {
            EventBroker.unsubscribe('compoundChanged', getWorkspaceCompoundNames);
        };
    }, [getWorkspaceCompoundNames]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, [messages]);

    const updateCommandText = useCallback((evtName, args) => {
        if (args.inputWords) {
            setHeard(args.inputWords);
        }

        if (args.interpretedCmds) {
            const { results, error } = args.interpretedCmds;
            if (results.length === 0) {
                if (error) console.log(`Failed to interpret command: ${error}`);
                setNoCommand(true);
            } else {
                setNoCommand(false);
                setMessages((prev) => [
                    ...prev,
                    {
                        content: results.map(({ displayText }) => displayText).join('\n'),
                        role: 'assistant',
                        id: prev.length,
                    },
                ]);
            }
        }
    }, []);

    useEventBrokerSubscription('commandInput', updateCommandText);

    // typed input
    const submitTypedCommand = useCallback(async () => {
        applyWordsAsCommands(typedCommand.trim());
        setTypedCommand('');
    }, [typedCommand]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <DialogContentSection
                titleVariant="subtitle2"
                collapsible
                defaultExpand
                disablePaddingTop
                title="Voice Control"
                TitleProps={{ style: { padding: '0 .5rem' } }}
            >
                <>
                    <Typography style={{ fontSize: 'small', margin: '.5rem 0' }}>
                        Voice commands are available by clicking on the mic button in the top right
                        corner of the BMaps application.
                        Please be aware that while the mic is active
                        BMaps will listen to and interpret everything that it hears.
                        You can also use the textbox at the bottom of the command panel
                        to manually type in commands.
                    </Typography>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={shorten}
                                onChange={updateCookie}
                            />
                        )}
                        label="Skip voice control greeting"
                    />
                </>

            </DialogContentSection>

            {/* Available commands description */}
            <DialogContentSection
                titleVariant="subtitle2"
                collapsible
                disablePaddingTop
                title="Available commands"
                TitleProps={{ style: { padding: '0 .5rem' } }}
            >
                <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.875rem' }}>
                    <li>
                        Set focus view — e.g.
                        {' '}
                        <em>protein view</em>
                        {', '}
                        <em>ligand view</em>
                    </li>
                    <li>
                        Reset the view
                    </li>
                    <li>
                        Change residue or ligand bond styles — e.g.
                        {' '}
                        <em>wireframe</em>
                        {', '}
                        <em>ball and stick</em>
                    </li>
                    <li>
                        Set hotspots on or off
                    </li>
                    <li>
                        Change background color
                    </li>
                    <li>
                        Select a specific residue or ligand
                    </li>
                    <li>
                        Dock a compound
                    </li>
                    <li>
                        Minimize a compound
                    </li>
                    <li>
                        Switch focus compound
                    </li>
                    <li>
                        Enable/disable residue labels or change colors
                    </li>
                </ul>
            </DialogContentSection>

            <div style={{
                padding: '1rem',
                paddingTop: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: '300px',
                flexGrow: 1,
                borderTop: '2px solid lightgray',
            }}
            >
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.5rem',
                        border: '1px solid lightgray',
                        borderRadius: '4px 4px 0 0',
                        background: 'whitesmoke',
                    }}
                >
                    {messages.length === 0 ? (
                        <Typography style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                            No commands have been performed.
                        </Typography>
                    ) : (
                        messages.map((message) => (
                            <Box
                                key={message.id}
                                ref={
                                message.id === messages.length - 1
                                    ? messagesEndRef
                                    : null
                            }
                                style={{
                                    marginBottom: '4px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems:
                                    message.role === 'user'
                                        ? 'flex-end'
                                        : 'flex-start',
                                }}
                            >
                                <strong style={{ fontStyle: 'italic' }}>
                                    {message.role === 'user' ? 'You:' : 'Command:'}
                                </strong>
                                <Box
                                    sx={{
                                        border: '1px solid lightgray',
                                        borderRadius: '.3rem',
                                        maxWidth: '90%',
                                        wordWrap: 'break-word',
                                        background:
                                        message.role === 'user'
                                            ? 'white'
                                            : 'mintcream',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        padding: '0.3rem',
                                        marginTop: '2px',
                                        '& li': { marginLeft: '1rem' },
                                        '& pre': { whiteSpace: 'pre-wrap' },
                                    }}
                                >
                                    <Typography style={{ whiteSpace: 'pre-wrap' }}>
                                        {message.content}
                                    </Typography>
                                </Box>
                            </Box>
                        ))
                    )}
                </div>
                <div
                    style={{
                        padding: '0.5rem',
                        border: '1px solid lightgray',
                        borderRadius: '0 0 4px 4px',
                        background: 'white',
                    }}
                >
                    {heard && (
                    <Typography
                        style={{ fontStyle: 'italic', marginBottom: '0.25rem' }}
                    >
                        <strong>User: </strong>
                        {heard}
                    </Typography>
                    )}
                    {noCommand && (
                    <Typography color="error">
                        Command not recognized
                    </Typography>
                    )}

                    {compoundNames.length > 0 && (
                        <Typography
                            component="div"
                            style={{ fontsize: '0.8rem', marginTop: '0.25rem' }}
                        >
                            <strong>Compounds in workspace:</strong>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                {compoundNames.map((compoundItem) => (
                                    <li key={compoundItem.id}>
                                        {compoundItem.name}
                                    </li>
                                ))}
                            </ul>
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Type a command..."
                        value={typedCommand}
                        onChange={(e) => setTypedCommand(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submitTypedCommand();
                            }
                        }}
                        style={{ margin: '0.5rem 0' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>

                        <DialogSubmitButton
                            onClick={() => submitTypedCommand()}
                        >
                            Submit
                        </DialogSubmitButton>
                        <DialogCancelButton
                            onClick={() => {
                                setTypedCommand('');
                                setMessages([]);
                                setHeard('');
                            }}
                        >
                            Clear
                        </DialogCancelButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
